import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, FileText, TrendingUp, GitBranch } from 'lucide-react';
import SectionWrapper from '../shared/SectionWrapper';

const capabilities = [
  {
    icon: MessageSquare,
    title: 'Natural Language Queries',
    description: 'Ask anything about your ESG data in plain English — get instant answers, charts, and insights.',
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    icon: FileText,
    title: 'Instant Report Generation',
    description: 'Compliance-ready reports in 60 seconds. PDF, DOCX, or XBRL — auto-formatted to framework specs.',
    color: 'text-violet-500',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
  },
  {
    icon: TrendingUp,
    title: 'Predictive Compliance',
    description: 'AI flags compliance gaps 3 months ahead so you can act before they become penalties.',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  {
    icon: GitBranch,
    title: 'Smart Data Mapping',
    description: 'Auto-maps your data to 35+ ESG frameworks. One dataset, infinite compliance possibilities.',
    color: 'text-cyan-500',
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
  },
];

const stats = [
  { value: '10x',   label: 'Faster Reporting' },
  { value: '99.8%', label: 'Accuracy' },
  { value: '24/7',  label: 'AI Assistant' },
  { value: 'Zero',  label: 'Manual Mapping' },
];

const AIShowcaseSection = () => (
  <SectionWrapper id="ai-showcase" bgClass="bg-[#fafbfc] dark:bg-[#0a0f1a]" padding="py-10 md:py-12">

    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <h2
        className="font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight"
        style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
      >
        Your{' '}
        <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">
          AI ESG Co-Pilot
        </span>
      </h2>
      <p className="text-sm text-gray-500 dark:text-slate-400 sm:text-right sm:max-w-xs flex-shrink-0">
        From data collection to compliance reporting — AI handles the heavy lifting.
      </p>
    </div>

    {/* Capabilities grid */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {capabilities.map((c, i) => {
        const Icon = c.icon;
        return (
          <motion.div
            key={c.title}
            className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 px-4 py-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${c.bg}`}>
              <Icon className={`w-4 h-4 ${c.color}`} strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white leading-snug">{c.title}</div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400 leading-relaxed">{c.description}</div>
            </div>
          </motion.div>
        );
      })}
    </div>

    {/* Stats bar */}
    <motion.div
      className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/60 px-6 py-5"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      {stats.map((s) => (
        <div key={s.label} className="text-center">
          <div className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-blue-500">
            {s.value}
          </div>
          <div className="mt-0.5 text-xs font-medium text-gray-400 dark:text-slate-500 uppercase tracking-widest">{s.label}</div>
        </div>
      ))}
    </motion.div>

  </SectionWrapper>
);

export default AIShowcaseSection;
